import React, { useEffect } from "react";
import PropTypes from "prop-types";
import TitlePages from "../../layout/TitlePages";
import Breadcrumbs from "../../layout/Breadcrumbs";
import "./Integracion.css";

const TabButton = ({ id, target, label, active }) => (
  <button
    className={`btn btn-ite integracion-tab-btn ${active ? "active" : ""}`}
    id={id}
    data-bs-toggle="pill"
    data-bs-target={target}
    type="button"
    role="tab"
    aria-controls={target.slice(1)}
    aria-selected={active ? "true" : "false"}
  >
    {label}
  </button>
);

const GridItem = ({ href, imgSrc, imgSrcSm, fallbackSrc, altText, title, name, index }) => {
  const isLink = Boolean(href && href.trim() !== "");
  const CardElement = isLink ? "a" : "div";
  const linkAttributes = isLink
    ? {
        href,
        target: "_blank",
        rel: "noopener noreferrer",
        "aria-label": `${name} - ${title} (Ver síntesis curricular)`,
      }
    : {};

  // Prioridad de carga para las tarjetas de la primera fila en desktop (índice 0 a 3)
  const isPriority = index < 4;

  const handleImageError = (e) => {
    if (fallbackSrc && !e.currentTarget.dataset.triedFallback) {
      e.currentTarget.dataset.triedFallback = "true";
      e.currentTarget.removeAttribute("srcset");
      e.currentTarget.src = fallbackSrc;
    }
  };

  return (
    <div className="integracion-card-wrapper">
      <CardElement
        {...linkAttributes}
        className={`integracion-card ${isLink ? "integracion-card--interactive" : ""}`}
      >
        <div className="integracion-card__img-container">
          <img
            src={imgSrc}
            srcSet={imgSrcSm ? `${imgSrcSm} 360w, ${imgSrc} 640w` : undefined}
            sizes="(max-width: 575px) 340px, (max-width: 991px) 380px, 320px"
            alt={altText || name}
            className="integracion-card__img"
            loading={isPriority ? "eager" : "lazy"}
            decoding="async"
            fetchPriority={index === 0 ? "high" : "auto"}
            width="320"
            height="400"
            onError={handleImageError}
          />
        </div>
        <div className="integracion-card__info">
          <div className="integracion-card__name">{name}</div>
          <div className="integracion-card__role">{title}</div>
          {isLink && (
            <div className="integracion-card__link">
              <span className="integracion-card__link-text">Síntesis curricular</span>
              <span className="integracion-card__link-arrow" aria-hidden="true">↗</span>
            </div>
          )}
        </div>
      </CardElement>
    </div>
  );
};

/* ==========================================================================
   Paletas institucionales por partido político
   (Fácil mantenimiento: para añadir un partido nuevo basta con definirlo aquí)
   ========================================================================== */
export const PARTY_PALETTES = {
  pan: {
    color: "#0B3FA8",
    oscuro: "#082C78",
    claro: "#3F72D6",
    textoOscuro: false,
  },
  pri: {
    color: "#00923F",
    oscuro: "#006B2E",
    claro: "#3FBF72",
    textoOscuro: false,
  },
  pt: {
    color: "#E11B22",
    oscuro: "#A3121A",
    claro: "#F2575C",
    textoOscuro: false,
  },
  pvem: {
    color: "#7E9928",
    oscuro: "#4C6415",
    claro: "#9CB939",
    textoOscuro: false,
  },
  mc: {
    color: "#E6730A",
    oscuro: "#B85500",
    claro: "#F28C28",
    textoOscuro: false,
  },
  pac: {
    color: "#7B2481",
    oscuro: "#521457",
    claro: "#9C3EA4",
    textoOscuro: false,
  },
  na: {
    color: "#188D88",
    oscuro: "#105F5C",
    claro: "#2BB5B0",
    textoOscuro: false,
  },
  morena: {
    color: "#8A1538",
    oscuro: "#5F0E27",
    claro: "#B84A66",
    textoOscuro: false,
  },
  rsp: {
    color: "#C62828",
    oscuro: "#8E1616",
    claro: "#E54D4D",
    textoOscuro: false,
  },
  fxm: {
    color: "#D4367A",
    oscuro: "#A31A56",
    claro: "#E8528F",
    textoOscuro: false,
  },
  prd: {
    color: "#C28E00",
    oscuro: "#8A6400",
    claro: "#E5B016",
    textoOscuro: false, // Ámbar dorado profundo con alto contraste sobre blanco (>4.6:1)
  },
  paz: {
    color: "#481858",
    oscuro: "#2D0D38",
    claro: "#6B2A80",
    textoOscuro: false,
  },
  somos: {
    color: "#A30050",
    oscuro: "#7A003B",
    claro: "#E80878",
    textoOscuro: false,
  },
};

export const getPartyPalette = (party) => {
  if (party.palette) return party.palette;
  if (party.paletteKey && PARTY_PALETTES[party.paletteKey]) {
    return PARTY_PALETTES[party.paletteKey];
  }
  const nameNorm = (party.name || "").toLowerCase();
  const foundKey = Object.keys(PARTY_PALETTES).find((k) => nameNorm.includes(k));
  if (foundKey) return PARTY_PALETTES[foundKey];
  return {
    color: party.accentColor || "#0B3FA8",
    oscuro: "#082C78",
    claro: "#3F72D6",
    textoOscuro: false,
  };
};

const PartyCard = ({
  href,
  logoSrc,
  fallbackLogo,
  altText,
  name,
  paletteKey,
  accentColor,
  palette,
  propietario,
  suplente,
}) => {
  const isLink = Boolean(href && href.trim() !== "");
  const CardContainer = isLink ? "a" : "div";
  const linkAttributes = isLink
    ? {
        href,
        target: "_blank",
        rel: "noopener noreferrer",
        "aria-label": `${name} - Representantes ante el Consejo General del ITE (Visitar sitio web oficial)`,
      }
    : {};

  const pal = getPartyPalette({ name, paletteKey, palette, accentColor });
  const nameKey = (paletteKey || name || "p").replace(/[^a-zA-Z0-9]/g, "");

  const handleImageError = (e) => {
    if (fallbackLogo && !e.currentTarget.dataset.triedFallback) {
      e.currentTarget.dataset.triedFallback = "true";
      e.currentTarget.src = fallbackLogo;
    }
  };

  return (
    <div className="stileintegracion-card-wrapper">
        <CardContainer
        {...linkAttributes}
        className={`stileintegracion-card ${isLink ? "stileintegracion-card--interactive" : ""} stileintegracion-card--${paletteKey || "default"}`}
        style={{
          "--color-partido": pal.color,
          "--color-partido-oscuro": pal.oscuro,
          "--color-partido-claro": pal.claro,
          "--color-partido-texto": pal.textoOscuro ? "#1F1A24" : "#FFFFFF",
          "--color-partido-acento": pal.textoOscuro ? "#8A6400" : (pal.acento || "#FFE58F"),
        }}
      >
        {/* A) ZONA SUPERIOR (fondo con formas de cristal/seda + onda única + logo) */}
        <div className="stileintegracion-card__top">
          {/* Capa de formas de cristal / seda translúcidas diagonales */}
          <div className="stileintegracion-card__crystal-container" aria-hidden="true">
            <svg
              className="stileintegracion-card__crystal-svg"
              viewBox="0 0 500 240"
              preserveAspectRatio="none"
            >
              <defs>
                <linearGradient id={`crystal-g1-${nameKey}`} x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stopColor="var(--color-partido-claro)" stopOpacity="0.22" />
                  <stop offset="100%" stopColor="var(--color-partido)" stopOpacity="0.03" />
                </linearGradient>
                <linearGradient id={`crystal-g2-${nameKey}`} x1="100%" y1="0%" x2="0%" y2="100%">
                  <stop offset="0%" stopColor="var(--color-partido)" stopOpacity="0.18" />
                  <stop offset="100%" stopColor="var(--color-partido-oscuro)" stopOpacity="0.02" />
                </linearGradient>
                <linearGradient id={`crystal-g3-${nameKey}`} x1="0%" y1="50%" x2="100%" y2="50%">
                  <stop offset="0%" stopColor="var(--color-partido-claro)" stopOpacity="0.14" />
                  <stop offset="100%" stopColor="transparent" stopOpacity="0" />
                </linearGradient>
                <linearGradient id={`crystal-g4-${nameKey}`} x1="100%" y1="50%" x2="0%" y2="50%">
                  <stop offset="0%" stopColor="var(--color-partido)" stopOpacity="0.12" />
                  <stop offset="100%" stopColor="transparent" stopOpacity="0" />
                </linearGradient>
              </defs>
              <path
                className="stileintegracion-crystal-shape stileintegracion-crystal-shape--1"
                fill={`url(#crystal-g1-${nameKey})`}
                d="M -40,30 C 100,-20 280,140 540,50 L 520,170 C 320,220 120,90 -40,130 Z"
              />
              <path
                className="stileintegracion-crystal-shape stileintegracion-crystal-shape--2"
                fill={`url(#crystal-g2-${nameKey})`}
                d="M -30,150 C 140,80 340,230 530,130 L 520,240 L -40,240 Z"
              />
              <path
                className="stileintegracion-crystal-shape stileintegracion-crystal-shape--3"
                fill={`url(#crystal-g3-${nameKey})`}
                d="M 60,-30 C 220,50 380,-20 540,80 L 530,-30 Z"
              />
              <path
                className="stileintegracion-crystal-shape stileintegracion-crystal-shape--4"
                fill={`url(#crystal-g4-${nameKey})`}
                d="M -30,0 C 160,80 340,10 520,70 L 510,120 C 330,60 150,130 -30,60 Z"
              />
            </svg>
          </div>

          {/* Curva de separación de un solo lomo amplio y fluido (1 a 1.5 ondulaciones) */}
          <div className="stileintegracion-card__waves-container" aria-hidden="true">
            <svg
              className="stileintegracion-card__waves-svg"
              viewBox="0 0 1200 120"
              preserveAspectRatio="none"
            >
              {/* Capa trasera desfasada (~30% opacidad) */}
              <path
                className="stileintegracion-wave-single--back"
                d="M 0,38 C 360,38 540,88 800,88 C 1000,88 1110,62 1200,62 L 1200,140 L 0,140 Z"
              />
              {/* Capa delantera sólida conectada con zona inferior */}
              <path
                className="stileintegracion-wave-single--front"
                d="M 0,46 C 380,46 520,96 820,96 C 1020,96 1120,70 1200,70 L 1200,140 L 0,140 Z"
              />
            </svg>
          </div>

          {/* Logo centrado directamente sobre el fondo claro con efecto 3D o placeholder discreto */}
          <div className="stileintegracion-card__logo-stage">
            {logoSrc ? (
              <img
                src={logoSrc}
                alt={altText || name}
                className={`stileintegracion-card__image ${paletteKey ? `stileintegracion-card__image--${paletteKey}` : ""}`}
                loading="lazy"
                decoding="async"
                onError={handleImageError}
              />
            ) : (
              <div
                className="stileintegracion-card__logo-placeholder"
                role="img"
                aria-label={altText || `Espacio reservado para logotipo de ${name}`}
              >
                <svg
                  className="stileintegracion-card__placeholder-icon"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <rect x="3" y="3" width="18" height="18" rx="3" ry="3" />
                  <circle cx="8.5" cy="8.5" r="1.5" />
                  <polyline points="21 15 16 10 5 21" />
                </svg>
                <span className="stileintegracion-card__placeholder-text">Logotipo pendiente</span>
              </div>
            )}
          </div>
        </div>

        {/* C) ZONA INFERIOR (degradado del color del partido y distribución vertical completa) */}
        <div className="stileintegracion-card__bottom">
          {/* Bloque 1: Título de partido + línea de acento */}
          <div className="stileintegracion-card__header-block">
            <div className="PartidosTi stileintegracion-card__title" role="heading" aria-level="3">
              {name}
            </div>
            <div className="stileintegracion-card__divider" aria-hidden="true" />
          </div>

          {/* Bloque 2: Representante Propietario / Propietaria */}
          <div className="stileintegracion-card__representative stileintegracion-card__representative--propietario">
            <span className="stileintegracion-card__role">
              {propietario?.cargo || "REPRESENTANTE PROPIETARIO"}
            </span>
            <span className="stileintegracion-card__name">
              {propietario?.nombre || "—"}
            </span>
          </div>

          {/* Bloque 3: Representante Suplente */}
          <div className="stileintegracion-card__representative stileintegracion-card__representative--suplente">
            <span className="stileintegracion-card__role">
              {suplente?.cargo || "REPRESENTANTE SUPLENTE"}
            </span>
            <span className="stileintegracion-card__name">
              {suplente?.nombre || "—"}
            </span>
          </div>

          {/* Bloque 4: Acción con línea divisoria y botón "Sitio oficial" o espaciador para alinear cards */}
          {isLink ? (
            <div className="stileintegracion-card__action">
              <span className="stileintegracion-card__link">
                <span className="stileintegracion-card__link-text">Sitio oficial</span>
                <span className="stileintegracion-card__link-arrow" aria-hidden="true">↗</span>
              </span>
            </div>
          ) : (
            <div className="stileintegracion-card__action stileintegracion-card__action--empty" aria-hidden="true" />
          )}
        </div>
      </CardContainer>
    </div>
  );
};

const Integración = () => {
  useEffect(() => {
    document.title = "Integración";

    if (typeof window === "undefined") return;
    const isReduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (isReduced) return;

    // Obtener todas las tarjetas de partidos políticos
    const cards = Array.from(document.querySelectorAll(".stileintegracion-card"));
    if (!cards.length) return;

    // Estado individual por tarjeta para respiración lenta a velocidad constante
    const cardStates = cards.map((card, idx) => {
      const waveBack = card.querySelector(".stileintegracion-wave-single--back");
      const waveFront = card.querySelector(".stileintegracion-wave-single--front");
      const c1 = card.querySelector(".stileintegracion-crystal-shape--1");
      const c2 = card.querySelector(".stileintegracion-crystal-shape--2");
      const c3 = card.querySelector(".stileintegracion-crystal-shape--3");
      const c4 = card.querySelector(".stileintegracion-crystal-shape--4");
      return {
        card,
        waveBack,
        waveFront,
        c1,
        c2,
        c3,
        c4,
        idx,
        isVisible: true,
      };
    });

    // IntersectionObserver para pausar tarjetas fuera de pantalla
    let observer;
    if ("IntersectionObserver" in window) {
      observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            const st = cardStates.find((s) => s.card === entry.target);
            if (st) st.isVisible = entry.isIntersecting;
          });
        },
        { threshold: 0.05 }
      );
      cards.forEach((card) => observer.observe(card));
    }

    let animId;

    const animate = (currentTime) => {
      // Pausar si la pestaña del navegador no está activa
      if (document.visibilityState !== "hidden") {
        for (let i = 0; i < cardStates.length; i++) {
          const s = cardStates[i];
          if (!s.isVisible) continue;

          // Animación continua a velocidad constante (sin alteración por cursor)
          const tWave = currentTime * 0.0005 + s.idx * 0.8;
          const waveBackX = Math.sin(tWave) * 12;
          const waveBackY = Math.cos(tWave * 0.85) * 5;
          const waveFrontX = Math.sin(tWave + 0.6) * 10;
          const waveFrontY = Math.cos(tWave * 0.9 + 0.4) * 4;

          if (s.waveBack) {
            s.waveBack.style.transform = `translate3d(${waveBackX.toFixed(2)}px, ${waveBackY.toFixed(2)}px, 0)`;
          }
          if (s.waveFront) {
            s.waveFront.style.transform = `translate3d(${waveFrontX.toFixed(2)}px, ${waveFrontY.toFixed(2)}px, 0)`;
          }

          // Flotación muy sutil y suave de las formas de cristal
          const tCrystal = currentTime * 0.00035 + s.idx * 0.5;
          if (s.c1) {
            const x1 = Math.sin(tCrystal) * 9;
            const y1 = Math.cos(tCrystal * 0.9) * 6;
            const r1 = Math.sin(tCrystal * 0.7) * 0.7; // rotación máxima <= 0.7°
            s.c1.style.transform = `translate3d(${x1.toFixed(2)}px, ${y1.toFixed(2)}px, 0) rotate(${r1.toFixed(2)}deg)`;
          }
          if (s.c2) {
            const x2 = Math.cos(tCrystal * 0.8 + 1.2) * 8;
            const y2 = Math.sin(tCrystal + 0.8) * 6;
            s.c2.style.transform = `translate3d(${x2.toFixed(2)}px, ${y2.toFixed(2)}px, 0)`;
          }
          if (s.c3) {
            const x3 = Math.sin(tCrystal * 1.1 + 2.1) * 9;
            const y3 = Math.cos(tCrystal * 0.7 + 1.5) * 5;
            s.c3.style.transform = `translate3d(${x3.toFixed(2)}px, ${y3.toFixed(2)}px, 0)`;
          }
          if (s.c4) {
            const x4 = Math.cos(tCrystal * 0.9 + 3.0) * 7;
            const y4 = Math.sin(tCrystal * 1.2 + 2.5) * 4;
            s.c4.style.transform = `translate3d(${x4.toFixed(2)}px, ${y4.toFixed(2)}px, 0)`;
          }
        }
      }

      animId = requestAnimationFrame(animate);
    };

    animId = requestAnimationFrame(animate);

    return () => {
      if (animId) cancelAnimationFrame(animId);
      if (observer) observer.disconnect();
    };
  }, []);

  const publicUrl = process.env.PUBLIC_URL || "";

  const gridItems = [
    {
      href: "https://itetlax.org.mx/assets/pdf/CG/1.pdf",
      imgSrc: `${publicUrl}/images/integracion/webp/1.webp`,
      imgSrcSm: `${publicUrl}/images/integracion/webp/sm/1.webp`,
      fallbackSrc: "https://itetlax.org.mx/assets/img/intCG/1.webp",
      altText: "Emmanuel Ávila González",
      title: "Consejero Presidente",
      name: "Lic. Emmanuel Ávila González",
    },
    {
      href: "",
      imgSrc: `${publicUrl}/images/integracion/webp/2.webp`,
      imgSrcSm: `${publicUrl}/images/integracion/webp/sm/2.webp`,
      fallbackSrc: "https://itetlax.org.mx/assets/img/intCG/2.jpg",
      altText: "Mtra. Arely Meza Granados.",
      title: "Secretaria Ejecutiva",
      name: "Mtra. Arely Meza Granados.",
    },
    {
      href: "https://itetlax.org.mx/assets/pdf/CG/4.pdf",
      imgSrc: `${publicUrl}/images/integracion/webp/5.webp`,
      imgSrcSm: `${publicUrl}/images/integracion/webp/sm/5.webp`,
      fallbackSrc: "https://itetlax.org.mx/assets/img/intCG/5.png",
      altText: "Janet Cervante Ahuatzi",
      title: "Consejera Electoral",
      name: "Lic. Janet Cervantes Ahuatzi",
    },
    {
      href: "https://itetlax.org.mx/assets/pdf/CG/5.pdf",
      imgSrc: `${publicUrl}/images/integracion/webp/6.webp`,
      imgSrcSm: `${publicUrl}/images/integracion/webp/sm/6.webp`,
      fallbackSrc: "https://itetlax.org.mx/assets/img/intCG/6.png",
      altText: "Hermenegildo Neria Carreño",
      title: "Consejero Electoral",
      name: "Lic. Hermenegildo Neria Carreño",
    },
    {
      href: "https://itetlax.org.mx/assets/pdf/CG/6.pdf",
      imgSrc: `${publicUrl}/images/integracion/webp/7.webp`,
      imgSrcSm: `${publicUrl}/images/integracion/webp/sm/7.webp`,
      fallbackSrc: "https://itetlax.org.mx/assets/img/intCG/7.png",
      altText: "Yedith Martínez Pinillo",
      title: "Consejera Electoral",
      name: "Lic. Yedith Martínez Pinillo",
    },
    {
      href: "https://itetlax.org.mx/assets/pdf/CG/7.pdf",
      imgSrc: `${publicUrl}/images/integracion/webp/8.webp`,
      imgSrcSm: `${publicUrl}/images/integracion/webp/sm/8.webp`,
      fallbackSrc: "https://itetlax.org.mx/assets/img/intCG/8.png",
      altText: "Dra. Anakaren Monserrat Rojas Cuautle",
      title: "Consejera Electoral",
      name: "Dra. Anakaren Monserrat Rojas Cuautle",
    },
    {
      href: "https://itetlax.org.mx/assets/pdf/CG/9.pdf",
      imgSrc: `${publicUrl}/images/integracion/webp/9.webp`,
      imgSrcSm: `${publicUrl}/images/integracion/webp/sm/9.webp`,
      fallbackSrc: "https://itetlax.org.mx/assets/img/intCG/9.png",
      altText: "Mtra. Brenda Fernanda Barrón Rugerio",
      title: "Consejera Electoral",
      name: "Mtra. Brenda Fernanda Barrón Rugerio",
    },
  ];

  const partyRepresentatives = [
    {
      name: "Partido Acción Nacional",
      paletteKey: "pan",
      href: "https://www.pantlaxcala.org.mx/",
      logoSrc: `${publicUrl}/images/iconsPP/logo_pan.png`,
      fallbackLogo: "https://itetlax.org.mx/assets/img/iconsPP/logo_pan.png",
      altText: "Logotipo de Partido Acción Nacional",
      accentColor: "#0B3FA8",
      propietario: {
        cargo: "REPRESENTANTE PROPIETARIO",
        nombre: "Lic. José Félix Solís Morales",
      },
      suplente: {
        cargo: "REPRESENTANTE SUPLENTE",
        nombre: "Lic. Ángelo Gutiérrez Hernández",
      },
    },
    {
      name: "Partido Revolucionario Institucional",
      paletteKey: "pri",
      href: "https://pritlaxcala.org.mx/",
      logoSrc: `${publicUrl}/images/iconsPP/logo_pri.png`,
      fallbackLogo: "https://itetlax.org.mx/assets/img/iconsPP/logo_pri.png",
      altText: "Logotipo de Partido Revolucionario Institucional",
      accentColor: "#00923F",
      propietario: {
        cargo: "REPRESENTANTE PROPIETARIO",
        nombre: "Lic. Frank Cuapio Pérez",
      },
      suplente: {
        cargo: "REPRESENTANTE SUPLENTE",
        nombre: "Lic. Noé Cuapio Cuahutle",
      },
    },
    {
      name: "Partido del Trabajo",
      paletteKey: "pt",
      href: "https://www.facebook.com/PTTlax1/",
      logoSrc: `${publicUrl}/images/iconsPP/logo_pt.png`,
      fallbackLogo: "https://itetlax.org.mx/assets/img/iconsPP/logo_pt.png",
      altText: "Logotipo de Partido del Trabajo",
      accentColor: "#E11B22",
      propietario: {
        cargo: "REPRESENTANTE PROPIETARIA",
        nombre: "Licda. Irma Yordana Garay Loredo",
      },
      suplente: {
        cargo: "REPRESENTANTE SUPLENTE",
        nombre: "Licda. Cynthia Navarrete Rubio",
      },
    },
    {
      name: "Partido Verde Ecologista de México",
      paletteKey: "pvem",
      href: "https://www.partidoverde.org.mx/representantes/552-regiones/16166-tlaxcala",
      logoSrc: `${publicUrl}/images/iconsPP/logo_pvem.png`,
      fallbackLogo: "https://itetlax.org.mx/assets/img/iconsPP/logo_pvem.png",
      altText: "Logotipo de Partido Verde Ecologista de México",
      accentColor: "#9CB939",
      propietario: {
        cargo: "REPRESENTANTE PROPIETARIO",
        nombre: "Lic. Jesús Portillo Herrera",
      },
      suplente: {
        cargo: "REPRESENTANTE SUPLENTE",
        nombre: "Lic. David Carrasco García",
      },
    },
    {
      name: "Partido Movimiento Ciudadano",
      paletteKey: "mc",
      href: "https://www.facebook.com/MovCiudadanoTlax/",
      logoSrc: `${publicUrl}/images/iconsPP/logo_mc.png`,
      fallbackLogo: "https://itetlax.org.mx/assets/img/iconsPP/logo_mc.png",
      altText: "Logotipo de Partido Movimiento Ciudadano",
      accentColor: "#E6730A",
      propietario: {
        cargo: "REPRESENTANTE PROPIETARIA",
        nombre: "Licda. Inori Rivera Ramírez",
      },
      suplente: {
        cargo: "REPRESENTANTE SUPLENTE",
        nombre: "Lic. Marco Antonio Olvera Ortega",
      },
    },
    {
      name: "Partido Alianza Ciudadana",
      paletteKey: "pac",
      href: "https://www.partidoalianzaciudadanatlax.com/",
      logoSrc: `${publicUrl}/images/iconsPP/logo_pac.png`,
      fallbackLogo: "https://itetlax.org.mx/assets/img/iconsPP/logo_pac.png",
      altText: "Logotipo de Partido Alianza Ciudadana",
      accentColor: "#7B2481",
      propietario: {
        cargo: "REPRESENTANTE PROPIETARIO",
        nombre: "Mtro. David Castillo Baltazares",
      },
      suplente: {
        cargo: "REPRESENTANTE SUPLENTE",
        nombre: "Lic. Kevin Henry Ramírez Mendieta",
      },
    },
    {
      name: "Partido Nueva Alianza Tlaxcala",
      paletteKey: "na",
      href: "https://www.facebook.com/NATlaxcala/",
      logoSrc: `${publicUrl}/images/iconsPP/logo_na.png`,
      fallbackLogo: "https://itetlax.org.mx/assets/img/iconsPP/logo_na.png",
      altText: "Logotipo de Partido Nueva Alianza Tlaxcala",
      accentColor: "#188D88",
      propietario: {
        cargo: "REPRESENTANTE PROPIETARIO",
        nombre: "Lic. Daniel López Nolasco",
      },
      suplente: {
        cargo: "REPRESENTANTE SUPLENTE",
        nombre: "Lic. José Efraín Hernándes de la Rosa",
      },
    },
    {
      name: "Partido Morena",
      paletteKey: "morena",
      href: "https://www.facebook.com/morenatlaxcalaoficial/",
      logoSrc: `${publicUrl}/images/iconsPP/logo_morena.png`,
      fallbackLogo: "https://itetlax.org.mx/assets/img/iconsPP/logo_morena.png",
      altText: "Logotipo de Morena",
      accentColor: "#8A1538",
      propietario: {
        cargo: "REPRESENTANTE PROPIETARIO",
        nombre: "Lic. Noé Hernández Rojas",
      },
      suplente: {
        cargo: "REPRESENTANTE SUPLENTE",
        nombre: "Lic. José Ventura Méndez Hernández",
      },
    },
    {
      name: "Partido Redes Sociales Progresistas Tlaxcala",
      paletteKey: "rsp",
      href: "https://www.facebook.com/RSPTlaxOficial/",
      logoSrc: `${publicUrl}/images/iconsPP/logo_rsp.png`,
      fallbackLogo: "https://itetlax.org.mx/assets/img/iconsPP/logo_rsp.png",
      altText: "Logotipo de Partido Redes Sociales Progresistas Tlaxcala",
      accentColor: "#C62828",
      propietario: {
        cargo: "REPRESENTANTE PROPIETARIO",
        nombre: "Lic. Hugo Marseille Ortega Vargas",
      },
      suplente: {
        cargo: "REPRESENTANTE SUPLENTE",
        nombre: "Lic. María Tania Sánchez Flores",
      },
    },
    {
      name: "Partido Fuerza por México Tlaxcala",
      paletteKey: "fxm",
      href: "https://www.facebook.com/fxmtlaxcala/",
      logoSrc: `${publicUrl}/images/iconsPP/logo_fxm.png`,
      fallbackLogo: "https://itetlax.org.mx/assets/img/iconsPP/logo_fxm.png",
      altText: "Logotipo de Partido Fuerza por México Tlaxcala",
      accentColor: "#D4367A",
      propietario: {
        cargo: "REPRESENTANTE PROPIETARIO",
        nombre: "Lic. Victor Delfino Moreno Rivera",
      },
      suplente: {
        cargo: "REPRESENTANTE SUPLENTE",
        nombre: "Licda. Lizbeth Tejeda de la Luz",
      },
    },
    {
      name: "Partido de la Revolución Democrática Tlaxcala",
      paletteKey: "prd",
      href: "https://www.facebook.com/PRDTlax/",
      logoSrc: `${publicUrl}/images/iconsPP/logo_prd.png`,
      fallbackLogo: "https://itetlax.org.mx/assets/img/iconsPP/logo_prd.png",
      altText: "Logotipo de Partido de la Revolución Democrática Tlaxcala",
      accentColor: "#C28E00",
      propietario: {
        cargo: "REPRESENTANTE PROPIETARIO",
        nombre: "Lic. Sergio Juárez Fragoso",
      },
      suplente: {
        cargo: "REPRESENTANTE SUPLENTE",
        nombre: "Lic. Gamaliel Sánchez Carreto",
      },
    },
    {
      name: "Partido Paz",
      paletteKey: "paz",
      href: "https://partidopaz.mx/#inicio",
      logoSrc: `${publicUrl}/images/iconsPP/logo_paz.png`,
      fallbackLogo: "https://itetlax.org.mx/assets/img/iconsPP/logo_paz.png",
      altText: "Logotipo de Partido Paz",
      accentColor: "#481858",
      propietario: {
        cargo: "REPRESENTANTE PROPIETARIO",
        nombre: "Lic. Luis Alberto Montes Mora",
      },
      suplente: {
        cargo: "REPRESENTANTE SUPLENTE",
        nombre: "Lic. Blanca Karina Sánchez López",
      },
    },
    {
      name: "Partido Somos",
      paletteKey: "somos",
      href: "https://somosmx.org.mx/",
      logoSrc: `${publicUrl}/images/iconsPP/logo_somos.png`,
      fallbackLogo: "https://itetlax.org.mx/assets/img/iconsPP/logo_somos.png",
      altText: "Logotipo de Partido Somos",
      accentColor: "#A30050",
      propietario: {
        cargo: "REPRESENTANTE PROPIETARIO",
        nombre: "Lic. Jonhatan Pérez Montiel",
      },
      suplente: {
        cargo: "REPRESENTANTE SUPLENTE",
        nombre: "Lic. Luis Ernesto Bernal Estrada",
      },
    },
  ];

  return (
    <>
      <Breadcrumbs path={[{ label: "Integración" }]} />
      <TitlePages title="Integración" subTitle="" />
      <div className="margin-bottom-2 integracion-section">
        {/* Selector de pestañas moderno (ancho completo) */}
        <div className="integracion-header-nav">
          <div
            className="nav nav-pills integracion-nav-pills"
            id="v-pills-tab"
            role="tablist"
            aria-orientation="horizontal"
          >
            <TabButton
              id="v-pills-integracion-tab"
              target="#v-pills-integracion"
              label="Integración"
              active
            />
            <TabButton
              id="v-pills-pp-tab"
              target="#v-pills-pp"
              label="Representantes de los Partidos Políticos"
            />
          </div>
        </div>

        {/* Contenido en col-12 completo */}
        <div className="tab-content w-100" id="v-pills-tabContent">
          <div
            className="tab-pane fade show active"
            id="v-pills-integracion"
            role="tabpanel"
            aria-labelledby="v-pills-integracion-tab"
          >
            <div className="w-100 integracion-grid">
              {gridItems.map((item, index) => (
                <GridItem key={item.name} {...item} index={index} />
              ))}
            </div>
          </div>

          <div
            className="tab-pane fade"
            id="v-pills-pp"
            role="tabpanel"
            aria-labelledby="v-pills-pp-tab"
            tabIndex="0"
          >
            <div className="w-100 stileintegracion-grid">
              {partyRepresentatives.map((rep) => (
                <PartyCard key={rep.name} {...rep} />
              ))}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Integración;

TabButton.propTypes = {
  id: PropTypes.string.isRequired,
  target: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  active: PropTypes.bool,
};

GridItem.propTypes = {
  href: PropTypes.string.isRequired,
  imgSrc: PropTypes.string.isRequired,
  imgSrcSm: PropTypes.string,
  fallbackSrc: PropTypes.string,
  altText: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  index: PropTypes.number,
};

PartyCard.propTypes = {
  href: PropTypes.string,
  logoSrc: PropTypes.string,
  fallbackLogo: PropTypes.string,
  altText: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  paletteKey: PropTypes.string,
  accentColor: PropTypes.string,
  palette: PropTypes.shape({
    color: PropTypes.string,
    oscuro: PropTypes.string,
    claro: PropTypes.string,
    textoOscuro: PropTypes.bool,
  }),
  propietario: PropTypes.shape({
    cargo: PropTypes.string,
    nombre: PropTypes.string,
  }).isRequired,
  suplente: PropTypes.shape({
    cargo: PropTypes.string,
    nombre: PropTypes.string,
  }).isRequired,
};

